/**
 * Los fallos del puerto de identidad, por código y no por frase.
 *
 * Un adaptador no es un componente y no puede usar un hook, así que **no puede
 * traducir**. Devuelve un código; lo traduce quien lo enseña, con
 * `useMensajeDeAcceso`. Es el patrón que `sinTextoEnDuro.test.ts` describe en su
 * cabecera, y la razón por la que este archivo no tiene una sola frase dentro.
 *
 * El motivo por el que la lista es corta: cada código es algo que una persona
 * puede **hacer distinto**. Un `400` y un `500` se ven igual desde la pantalla
 * —«vuelve a intentar»— así que comparten código. `credenciales` y `red` no:
 * una se arregla escribiendo otra vez, la otra esperando.
 */

export const CODIGOS_DE_ACCESO = [
  /** El correo o la contraseña no coinciden. */
  'credenciales',
  /** No se llegó al servidor: sin red, DNS, CORS, o el servicio caído. */
  'red',
  /** Este entorno no tiene a quién preguntar. Ver `AdaptadorHttp`. */
  'sinConfigurar',
  /** Se llegó al servidor y contestó mal. */
  'servidor',
  /** Había sesión y ya no vale. */
  'sesionExpirada',
  /**
   * La sesión es válida pero no alcanza para esto: el 403 de
   * `/admin/inversionistas`.
   *
   * Es un código propio y no `generico` porque una persona hace algo
   * **distinto** con él: no reintentar ni volver a entrar, sino pedir que la
   * promuevan. Confundirlo con `sesionExpirada` manda a alguien a escribir su
   * contraseña otra vez para acabar exactamente igual.
   */
  'sinPermiso',
  /** El código no vale: no es el suyo, ya se usó, o se quemó a intentos. */
  'codigoInvalido',
  /** Era suyo pero pasaron los 15 minutos. Se pide otro, no se reintenta. */
  'codigoCaducado',
  /** La contraseña nueva es demasiado corta. */
  'contrasenaCorta',
  /** Lo que no encaja en los anteriores. */
  'generico',
] as const;

export type CodigoDeAcceso = (typeof CODIGOS_DE_ACCESO)[number];

/**
 * El único error que el puerto lanza.
 *
 * Que sea el único importa: la pantalla puede escribir `catch` una vez y
 * traducir siempre, sin ir preguntando de qué tipo es cada cosa. Un adaptador
 * que deje escapar un `TypeError` de `fetch` rompe ese contrato, y por eso los
 * adaptadores envuelven todo lo que no reconocen en `generico`.
 */
export class ErrorDeAcceso extends Error {
  readonly codigo: CodigoDeAcceso;
  /**
   * Lo que falló por debajo, si lo hubo.
   *
   * Va en un campo propio y no en el `cause` de `Error`, que sería lo natural:
   * `tsconfig.json` compila contra **ES2020** y esa opción es de ES2022, así que
   * usarla obligaría a subir el `lib` de todo el proyecto por una comodidad de
   * diagnóstico. No vale el cambio.
   */
  readonly causa?: unknown;

  constructor(codigo: CodigoDeAcceso, causa?: unknown) {
    // El `message` lleva el código y no una frase: si alguna vez acaba en un
    // log o en una consola, dice algo útil y no una traducción a medias.
    super(codigo);
    this.name = 'ErrorDeAcceso';
    this.codigo = codigo;
    this.causa = causa;
  }
}

/** Para el `catch` de quien enseña: convierte cualquier cosa en un código. */
export function codigoDe(error: unknown): CodigoDeAcceso {
  return error instanceof ErrorDeAcceso ? error.codigo : 'generico';
}
